import React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { CheckCircleIcon, CloseIcon, InfoIcon, WarningIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

type ToastAction = Readonly<{
	label: string | React.ReactNode;
	onClick: () => void;
}>;

type Props = Readonly<{
	variant: ToastVariant;
	message: string | React.ReactNode;
	action?: ToastAction;
	onActionClick: () => void;
	onClose?: () => void;
}>;

const variantStyles = {
	success: {
		bg: 'bg-fill-success-weaker',
		border: 'border-stroke-success',
		text: 'text-typography-success',
		iconClass: 'text-icon-success',
		Icon: CheckCircleIcon,
		maxWidth: 'max-w-3xl',
		bottomPosition: 'bottom-[2vh]',
		showCloseButton: false,
	},
	error: {
		bg: 'bg-fill-error-weaker',
		border: 'border-stroke-error-weak',
		text: 'text-typography-error',
		iconClass: 'text-icon-error',
		Icon: WarningIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[10vh]',
		showCloseButton: true,
	},
	warning: {
		bg: 'bg-fill-warning-subtle',
		border: 'border-stroke-warning',
		text: 'text-typography-warning',
		iconClass: 'text-icon-warning',
		Icon: WarningIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[10vh]',
		showCloseButton: true,
	},
	info: {
		bg: 'bg-fill-info-subtle',
		border: 'border-stroke-info',
		text: 'text-typography-primary',
		iconClass: 'text-icon-info',
		Icon: InfoIcon,
		maxWidth: 'max-w-xl',
		bottomPosition: 'bottom-[10vh]',
		showCloseButton: true,
	},
};

export const Toast: React.FC<Props> = ({ variant, message, action, onActionClick, onClose }) => {
	const currentVariant = variantStyles[variant];
	const IconComponent = currentVariant.Icon;

	return (
		<div
			className={cn(
				'fixed left-1/2 z-1000 mx-4 flex w-full -translate-x-1/2 flex-row items-center gap-4 rounded-lg border p-6',
				currentVariant.bottomPosition,
				currentVariant.maxWidth,
				currentVariant.bg,
				currentVariant.border,
			)}
		>
			<div className="flex flex-1 items-center gap-4">
				<div className="shrink-0">
					<IconComponent className={currentVariant.iconClass} />
				</div>
				<Text className={cn(currentVariant.text, 'font-bold')}>{message}</Text>
			</div>
			{action && (
				<Button className="h-9 shrink-0" onClick={onActionClick} variant="secondary-solid" size="small">
					<Text variant="body" size="medium">
						{action.label}
					</Text>
				</Button>
			)}
			{onClose && currentVariant.showCloseButton && (
				<button
					type="button"
					onClick={onClose}
					className={cn('flex shrink-0 items-center justify-center rounded p-1 hover:bg-black/10', currentVariant.text)}
					aria-label="Close"
				>
					<CloseIcon className="h-5 w-5" />
				</button>
			)}
		</div>
	);
};
